import { lazy, Suspense, useCallback, useEffect, useRef, useState } from "react"
import QRCode from "qrcode"
import { toast } from "react-toastify"
import { Box, HBox, VBox } from "@ui/box/Box"
import Button from "@ui/button/Button"
import { Typo } from "@ui/text/Typo"
import { GD } from "src/GD"
import { getFriendlyErrorMessage, isKycAlreadyApproved, isKycPhoneCorrectionError } from "src/utils/errors"
import { getKycStatusSnapshot, isKycInProgress, isOurKycTerminal } from "src/utils/kyc"
import { KycIssuersMatrix } from "./KycIssuersMatrix"
import { KycCallout, KycStepper, KycVerifyCard } from "./ProviderKycStatus"
import { DS, Glyph } from "../ds/Kit"
import { useOptionalIssuer } from "../issuer/IssuerProvider"
import { isRunningInNativeApp, openExternalUrl } from "src/utils/nativePlatform"

const KycPhoneForm = lazy(() => import("./KycPhoneForm").then(module => ({ default: module.KycPhoneForm })))

const POLL_INTERVAL_MS = 3000

/**
 * Self-contained KYC widget (task M-15): shows verification status, starts the
 * flow in a browser and shows the per-issuer matrix separately. Once our KYC
 * is approved it cannot be restarted from here; issuer approval is read-only.
 */
export const KycSection = ({ hideWhenApproved = false }: { hideWhenApproved?: boolean } = {}) => {
	const selectedIssuerId = useOptionalIssuer()?.selectedIssuerId ?? null
	return <IssuerKycSection key={selectedIssuerId ?? "default"} selectedIssuerId={selectedIssuerId} hideWhenApproved={hideWhenApproved} />
}

const IssuerKycSection = ({ selectedIssuerId, hideWhenApproved }: { selectedIssuerId: number | null; hideWhenApproved: boolean }) => {
	const nativeApp = isRunningInNativeApp()
	const [status, setStatus] = useState<TKycStatus | null>(null)
	const [reason, setReason] = useState<string | null>(null)
	const [loading, setLoading] = useState(true)
	const [starting, setStarting] = useState(false)
	const [confirmingReset, setConfirmingReset] = useState(false)
	const [verificationUrl, setVerificationUrl] = useState<string | null>(null)
	const [qrDataUrl, setQrDataUrl] = useState("")
	const [phoneRequired, setPhoneRequired] = useState(false)
	const timer = useRef<ReturnType<typeof setInterval> | null>(null)
	const mounted = useRef(false)
	useEffect(() => {
		mounted.current = true
		return () => { mounted.current = false }
	}, [])

	const stopPolling = useCallback(() => {
		if (timer.current) {
			clearInterval(timer.current)
			timer.current = null
		}
	}, [])

	const fetchStatus = useCallback(async () => {
		const res = await GD.REQ_KYC_STATUS.request({ issuer: selectedIssuerId ?? undefined })
		if (!mounted.current || res.error) return null
		const snapshot = getKycStatusSnapshot(res.data)
		setStatus(snapshot.ourStatus)
		setReason(snapshot.ourReason)
		if (isOurKycTerminal(snapshot.ourStatus)) {
			setVerificationUrl(null)
			stopPolling()
		}
		return snapshot
	}, [selectedIssuerId, stopPolling])

	const startPolling = useCallback(() => {
		stopPolling()
		timer.current = setInterval(fetchStatus, POLL_INTERVAL_MS)
	}, [fetchStatus, stopPolling])

	useEffect(() => {
		let active = true
		;(async () => {
			const s = await fetchStatus()
			if (!active) return
			setLoading(false)
			if (s && isKycInProgress(s.ourStatus)) {
				startPolling()
			}
		})()
		return () => {
			active = false
			stopPolling()
		}
	}, [fetchStatus, startPolling, stopPolling])

	useEffect(() => {
		let active = true
		setQrDataUrl("")
		if (!verificationUrl || nativeApp) return
		QRCode.toDataURL(verificationUrl, { width: 220, margin: 1 })
			.then(dataUrl => { if (active) setQrDataUrl(dataUrl) })
			.catch(() => { if (active) setQrDataUrl("") })
		return () => { active = false }
	}, [nativeApp, verificationUrl])

	const startKyc = useCallback(async (autoOpen: boolean = true) => {
		setStarting(true)
		const res = await GD.REQ_KYC_START.request({ issuer: selectedIssuerId ?? undefined })
		setStarting(false)

		if (res.error) {
			if (isKycPhoneCorrectionError(res.error)) {
				setPhoneRequired(true)
				return
			}
			if (isKycAlreadyApproved(res.error)) {
				setPhoneRequired(false)
				await fetchStatus()
				return
			}
			toast.error(getFriendlyErrorMessage(res.error, "Failed to start KYC"))
			return
		}

		const url = res.data.verification_url
		if (!url) {
			toast.error("Verification URL is missing")
			return
		}

		setVerificationUrl(url)
		setPhoneRequired(false)

		if (autoOpen && nativeApp) {
			try {
				const opened = await openExternalUrl(url)
				if (!opened) toast.info("Allow pop-ups, then use Open verification again")
			} catch (error) {
				console.error(error)
				toast.error("Could not open identity verification")
			}
		}
		setStatus("pending")
		setReason(null)
		setConfirmingReset(false)
		startPolling()
	}, [nativeApp, selectedIssuerId, startPolling])

	const continueKycAfterPhone = useCallback(async () => {
		setPhoneRequired(false)
		await startKyc(true)
	}, [startKyc])

	const handleStartClick = useCallback(() => {
		if (isKycInProgress(status) && verificationUrl) {
			void openExternalUrl(verificationUrl).then(opened => {
				if (!opened) toast.info("Allow pop-ups, then try again")
			}).catch(error => {
				console.error(error)
				toast.error("Could not open identity verification")
			})
			return
		}
		// Only an in-progress session needs a confirmation before being reset.
		if (isKycInProgress(status)) {
			setConfirmingReset(true)
			return
		}
		void startKyc(true)
	}, [status, verificationUrl, startKyc])

	// Private-company holders only need this section while KYC is unresolved;
	// once approved it's redundant with the onboarding gate. They're approved
	// in the common case, so stay hidden until we know it's NOT approved —
	// this also avoids flashing a "Checking status…" card on every open.
	if (hideWhenApproved && (loading || status === "approved")) return null
	if (phoneRequired) {
		return (
			<Box sx={{ backgroundColor: DS.surface, border: `1px solid ${DS.line}`, borderRadius: DS.radius, padding: "20px", boxShadow: DS.shadow }}>
				<VBox gap='18px'>
					<KycStepper current={0} />
					<Suspense fallback={<Typo variant='caption'>Loading phone form…</Typo>}>
						<KycPhoneForm
							issuer={selectedIssuerId ?? undefined}
							onSuccess={continueKycAfterPhone}
							onCancel={() => setPhoneRequired(false)}
							embedded
						/>
					</Suspense>
				</VBox>
			</Box>
		)
	}

	let statusBody: React.ReactNode = null
	let buttonTitle = "Start KYC"

	if (loading) {
		statusBody = <KycCallout tone='info' title='Checking status…' />
	} else if (isKycInProgress(status)) {
		buttonTitle = verificationUrl ? "Open verification" : "Restart verification"
		statusBody = (
			<KycCallout tone='pending' title='Verification in progress'>
				{nativeApp
					? "Complete verification in the opened browser, then return to Cardpartner. Status updates automatically."
					: verificationUrl
						? "Scan the QR code or open the verification form. This page stays open and updates automatically."
						: "Generate a new verification link to continue. This page updates automatically."}
			</KycCallout>
		)
	} else if (status === "approved") {
		statusBody = <KycCallout tone='approved' title='Identity verification approved' />
	} else if (status === "declined" || status === "expired") {
		buttonTitle = "Retry KYC"
		statusBody = (
			<KycCallout tone={status} title={status === "declined" ? "Verification declined" : "Verification expired"}>
				{reason}
			</KycCallout>
		)
	} else {
		// none / unknown — not started
		statusBody = (
			<KycCallout tone='info' title='Verify your identity'>
				{nativeApp
					? "Verify your identity to issue cards that require KYC. Verification opens in your browser and status updates here automatically."
					: "Verify your identity to issue cards that require KYC. A QR code appears here and status updates automatically."}
			</KycCallout>
		)
	}

	const actionArea = confirmingReset ? (
		<VBox
			gap='12px'
			sx={{
				backgroundColor: "#FFFBEB",
				border: "1px solid #FDE68A",
				borderRadius: "12px",
				padding: "14px 16px"
			}}
		>
			<Typo sx={{ color: "#92400E", fontSize: "13px", lineHeight: 1.5 }}>
				A verification is already in progress. Reset the current KYC session and
				start over?
			</Typo>
			<HBox gap='8px' sx={{ flexWrap: "wrap" }}>
				<Button
					title={starting ? "Starting…" : "Reset & start over"}
					variant='primary'
					disabled={starting}
					onClick={() => void startKyc()}
					sx={{ padding: "6px 14px", fontSize: "0.85rem", borderRadius: "10px" }}
				/>
				<Button
					title='Cancel'
					variant='secondary'
					disabled={starting}
					onClick={() => setConfirmingReset(false)}
					sx={{ padding: "6px 14px", fontSize: "0.85rem", borderRadius: "10px" }}
				/>
			</HBox>
		</VBox>
	) : (
		<HBox>
			<Button
				title={starting ? "Starting…" : buttonTitle}
				variant='primary'
				disabled={starting}
				onClick={handleStartClick}
				sx={{ padding: "8px 18px", fontSize: "0.9rem", borderRadius: "12px", minHeight: "44px" }}
			/>
		</HBox>
	)
	const showQr = !nativeApp && !loading && isKycInProgress(status) && Boolean(qrDataUrl) && Boolean(verificationUrl)

	return (
		<Box
			sx={{
				backgroundColor: DS.surface,
				border: `1px solid ${DS.line}`,
				borderRadius: DS.radius,
				padding: "20px",
				boxShadow: DS.shadow
			}}
		>
			<HBox gap='12px' sx={{ alignItems: "center", marginBottom: "18px" }}>
				<Box aria-hidden sx={{ width: "40px", height: "40px", minWidth: "40px", borderRadius: "12px", background: DS.accentSoft, color: DS.accent, display: "grid", placeItems: "center" }}>{Glyph.shield}</Box>
				<VBox gap='2px' sx={{ minWidth: 0 }}>
					<Typo sx={{ color: DS.ink, fontSize: "16px", fontWeight: 650 }}>Identity verification (KYC)</Typo>
					<Typo sx={{ color: DS.muted, fontSize: "13px" }}>Required to issue cards that need verified identity.</Typo>
				</VBox>
			</HBox>
			<VBox gap='16px'>
				<KycStepper current={status === "approved" ? 2 : 1} />
				{statusBody}
				{!loading && status === "approved" && <KycIssuersMatrix />}
				{!loading && status !== "approved" && (
					<KycVerifyCard
						qrDataUrl={showQr ? qrDataUrl : undefined}
						qrSize={160}
						hint='Scan to continue on your phone'
					>
						{actionArea}
					</KycVerifyCard>
				)}
			</VBox>
		</Box>
	)
}
